import { useEffect, useState } from "react";
import { AlertTriangle, IndianRupee, Loader2, Package, Pencil, Plus, Search, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import apiClient from "./api";
import { useAuth } from "./AuthContext";
import { imgUrl, inr } from "../store/CartContext";
import AdminProductEditor from "./AdminProductEditor";

export function StoreStats({ summary }) {
  if (!summary) return null;
  const cards = [
    [IndianRupee, "Revenue", inr(summary.revenue), "text-green-400"],
    [Package, "Paid orders", summary.paid_orders, "text-white"],
    [Truck, "To ship", summary.to_ship, "text-[#FC5106]"],
    [AlertTriangle, "Low stock items", summary.low_stock.length, "text-[#FFB547]"],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="store-stats">
      {cards.map(([I, l, v, c]) => (
        <div key={l} className="rounded-2xl border border-white/10 bg-[#121212] p-4">
          <I className={`mb-2 h-4 w-4 ${c}`} />
          <p className="text-xs uppercase tracking-wider text-white/50">{l}</p>
          <p className={`font-heading text-2xl font-bold ${c}`}>{v}</p>
        </div>
      ))}
    </div>
  );
}

export default function AdminProducts() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(undefined);
  const [summary, setSummary] = useState(null);

  const load = () => {
    apiClient.get("/api/admin/store/products", { params: { q: q || undefined } }).then((r) => setItems(r.data)).catch(() => toast.error("Could not load products"));
    apiClient.get("/api/admin/store/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try { await apiClient.delete(`/api/admin/store/products/${p.id}`); toast.success("Product deleted"); load(); } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 text-xs uppercase tracking-[0.25em] text-[#C9BCFF]">Shop</p>
          <h1 className="font-heading text-3xl font-bold text-white md:text-4xl">Products</h1>
        </div>
        <button onClick={() => setEditing(null)} data-testid="admin-product-add-button" className="btn-primary !px-4 !py-2.5 !text-sm"><Plus className="h-4 w-4" /> Add product</button>
      </div>
      <StoreStats summary={summary} />
      {summary?.low_stock.length > 0 && (
        <div data-testid="low-stock-alert" className="rounded-2xl border border-[#FFB547]/30 bg-[#FFB547]/5 p-4 text-sm text-[#FFB547]">
          <p className="mb-1 font-semibold">Low stock</p>
          {summary.low_stock.map((p) => <p key={p.id} className="text-white/70">{p.name}: {p.variants.map((v) => `${v.label} (${v.stock})`).join(", ")}</p>)}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="admin-product-search" className="w-full rounded-xl border border-white/10 bg-[#121212] py-2.5 pl-10 pr-3 text-sm text-white outline-none focus:border-[#FC5106]" />
      </div>
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#121212]">
        {!items ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#FC5106]" /></div> : items.length === 0 ? <p className="py-16 text-center text-sm text-white/50">No products yet.</p> : (
          <ul className="divide-y divide-white/5">
            {items.map((p) => (
              <li key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-4 px-4 py-3">
                <img src={imgUrl(p.images?.[0])} alt="" className="h-14 w-14 flex-shrink-0 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-white">{p.name} {!p.active && <span className="ml-1 rounded-full border border-white/15 px-2 text-[10px] uppercase text-white/50">Hidden</span>}</p>
                  <p className="text-xs text-white/50">{p.brand} · {p.category} · from {inr(p.min_price)}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {p.variants.map((v) => (
                      <span key={v.id} className={`rounded-full border px-2 py-0.5 text-[10px] ${v.stock === 0 ? "border-red-500/40 text-red-400" : v.stock <= (summary?.threshold ?? 5) ? "border-[#FFB547]/40 text-[#FFB547]" : "border-white/10 text-white/60"}`}>{v.label}: {v.stock}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => setEditing(p)} data-testid={`admin-product-edit-${p.id}`} className="rounded-lg p-2 text-white/60 hover:bg-white/5 hover:text-white" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                {user?.role === "admin" && <button onClick={() => remove(p)} data-testid={`admin-product-delete-${p.id}`} className="rounded-lg p-2 text-white/40 hover:bg-white/5 hover:text-red-400" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {editing !== undefined && <AdminProductEditor product={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}
